import { Button, Card, Descriptions, Table, Tag, message, Divider } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import LoadingBox from "../../Components/LoadingBox";
import { API } from "../../Config";
import { GET } from "../../Config/ApiRequests";
import PageHeader from "../../Routes/PageHeader";

const VoucherViewPage = () => {
  const { id, type } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [voucherData, setVoucherData] = useState(null);

  // Get voucher type label
  const getVoucherTypeLabel = (vType) => {
    switch (vType) {
      case "PV": return "Payment Voucher";
      case "RV": return "Receipt Voucher";
      case "JV": return "Journal Voucher";
      default: return vType || "-";
    }
  };

  // Get back URL based on voucher type
  const getBackUrl = () => {
    switch (type) {
      case "PV": return "/auth/VoucherPv/PV";
      case "RV": return "/auth/VoucherRv/RV";
      case "JV": return "/auth/VoucherJv/JV";
      default: return "/auth/VoucherPv/PV";
    }
  };

  useEffect(() => {
    if (id) {
      fetchVoucherDetails();
    }
  }, [id]);

  const fetchVoucherDetails = async () => {
    setLoading(true);
    try {
      const response = await GET(`${API.VOUCHER_DETAIL}${id}`);
      const data = response?.data || response;
      if (data?.id) {
        setVoucherData(data);
      } else {
        message.error("Failed to load voucher details");
      }
    } catch (error) {
      message.error("Failed to load voucher details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Voucher Details columns
  const detailColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Account",
      key: "account",
      width: "30%",
      render: (_, record) => {
        const accNo = record?.account_no || "-";
        const accName = record?.accounts?.account_name;
        return accName ? `${accNo} - ${accName}` : accNo;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text) => text || "-",
    },
    {
      title: "Dr Amount",
      dataIndex: "amount_dr",
      key: "amount_dr",
      width: "15%",
      align: "right",
      render: (v) => (v != null && v > 0 ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Cr Amount",
      dataIndex: "amount_cr",
      key: "amount_cr",
      width: "15%",
      align: "right",
      render: (v) => (v != null && v > 0 ? Number(v).toFixed(2) : "-"),
    },
  ];

  // Ledger Transaction columns
  const ledgerColumns = [
    {
      title: "#",
      key: "index",
      width: 40,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "trans_date",
      key: "trans_date",
      width: "10%",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Trans Type",
      dataIndex: "trans_type",
      key: "trans_type",
      width: "10%",
      render: (v) => v || "-",
    },
    {
      title: "Account",
      key: "account",
      width: "20%",
      render: (_, record) => {
        const accNo = record?.account_no || "-";
        const accName = record?.accounts?.account_name;
        return accName ? `${accNo} - ${accName}` : accNo;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "10%",
      align: "right",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Dr/Cr",
      dataIndex: "dr_or_cr",
      key: "dr_or_cr",
      width: "8%",
      align: "center",
      render: (v) => (
        <Tag color={v === "Dr" ? "green" : "red"}>{v || "-"}</Tag>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference_no",
      key: "reference_no",
      width: "12%",
      render: (text) => text || "-",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: "30%",
      ellipsis: true,
      render: (text) => text || "-",
    },
  ];

  if (loading) {
    return <LoadingBox />;
  }

  if (!voucherData) {
    return (
      <div>
        <PageHeader breadScrum={`Admin / ${getVoucherTypeLabel(type)} / View`} />
        <Card>
          <p>Voucher not found</p>
          <Button onClick={() => history.push(getBackUrl())}>
            Back to Voucher List
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader breadScrum={`Admin / ${getVoucherTypeLabel(voucherData?.voucher_type)} / View`} />
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push(getBackUrl())}
            >
              Back
            </Button>
            <span>{getVoucherTypeLabel(voucherData?.voucher_type)} Details</span>
            <Tag color="blue">{voucherData?.voucher_type}</Tag>
          </div>
        }
      >
        {/* Basic Info */}
        <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1 }}>
          <Descriptions.Item label="Voucher ID">{voucherData?.id || "-"}</Descriptions.Item>
          <Descriptions.Item label="Date">
            {voucherData?.voucher_date ? moment(voucherData?.voucher_date).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Voucher Type">
            <Tag color="blue">{getVoucherTypeLabel(voucherData?.voucher_type)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Account">
            {voucherData?.accounts
              ? `${voucherData.account_no} - ${voucherData.accounts.account_name}`
              : voucherData?.account_no || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Dr Total">
            <strong style={{ color: "#52c41a" }}>
              {voucherData?.dr_total != null ? Number(voucherData?.dr_total).toFixed(2) : "-"}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Cr Total">
            <strong style={{ color: "#f5222d" }}>
              {voucherData?.cr_total != null ? Number(voucherData?.cr_total).toFixed(2) : "-"}
            </strong>
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {voucherData?.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            {voucherData?.user?.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {voucherData?.createdat
              ? moment(voucherData?.createdat).format("DD-MM-YYYY HH:mm")
              : "-"}
          </Descriptions.Item>
        </Descriptions>

        {/* Voucher Details */}
        <div style={{ marginTop: 24 }}>
          <h3>Voucher Details</h3>
          <Table
            columns={detailColumns}
            dataSource={voucherData?.voucherDetails || []}
            pagination={false}
            size="small"
            rowKey="id"
            locale={{ emptyText: "No voucher details" }}
          />
        </div>

        {/* Ledger Transactions */}
        {voucherData?.ledgerTransactions?.length > 0 && (
          <>
            <Divider />
            <div style={{ marginTop: 24 }}>
              <h3>Ledger Transactions</h3>
              <Table
                columns={ledgerColumns}
                dataSource={voucherData?.ledgerTransactions || []}
                pagination={false}
                size="small"
                rowKey="id"
                locale={{ emptyText: "No ledger transactions" }}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default VoucherViewPage;
